import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import apiAxios, { apiAxiosUpload } from "helpers/apiAxios";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";

// Payment plans — the ONE place they are recorded, edited and released (Finance
// Security). A plan covers buildings with a P/J number (no Job Building needed)
// and maps to the contract covering them once it exists. Contract step / PMD
// only read them. (Route / state keep the `payment_schedules` name.)
const scheduleUrl = (projectId, scheduleId = null) => {
  const base = `/crm/projects/${projectId}/payment_schedules`;
  return scheduleId ? `${base}/${scheduleId}` : base;
};
const planUrl = (projectId, scheduleId, planId = null) => {
  const base = `${scheduleUrl(projectId, scheduleId)}/payment_plans`;
  return planId ? `${base}/${planId}` : base;
};
// Bank guarantee / L.C lives on the same schedule row, so it rides this slice
// rather than getting one of its own.
const guaranteeUrl = (projectId, scheduleId) => `${scheduleUrl(projectId, scheduleId)}/bank_guarantee`;

const thunk = (type, fallback, request) => createAsyncThunk(type, async (args, { rejectWithValue }) => {
  try {
    const response = await request(args);
    return response.data;
  } catch (error) {
    return rejectWithValue(handleAsyncThunkError(error, fallback));
  }
});

export const getPaymentSchedules = thunk(
  "PaymentSchedule/getPaymentSchedules", 'Failed to get payment plans',
  (projectId) => apiAxios.get(scheduleUrl(projectId))
);

export const getScheduleBuildingOptions = thunk(
  "PaymentSchedule/getScheduleBuildingOptions", 'Failed to load buildings',
  (projectId) => apiAxios.get(`${scheduleUrl(projectId)}/building_options`)
);

// `paymentPlan` (optional): recording the first payment creates the plan it
// belongs to, so there is no separate "create" step for the accountant.
export const createPaymentSchedule = thunk(
  "PaymentSchedule/createPaymentSchedule", 'Failed to create payment plan',
  ({ projectId, projIds, paymentPlan }) =>
    apiAxios.post(scheduleUrl(projectId), { proj_ids: projIds, payment_plan: paymentPlan })
);

export const updatePaymentSchedule = thunk(
  "PaymentSchedule/updatePaymentSchedule", 'Failed to update payment plan',
  ({ projectId, scheduleId, projIds }) => apiAxios.put(scheduleUrl(projectId, scheduleId), { proj_ids: projIds })
);

export const deletePaymentSchedule = thunk(
  "PaymentSchedule/deletePaymentSchedule", 'Failed to delete payment plan',
  ({ projectId, scheduleId }) => apiAxios.delete(scheduleUrl(projectId, scheduleId))
);

export const linkPaymentScheduleContract = thunk(
  "PaymentSchedule/linkPaymentScheduleContract", 'Failed to link the contract',
  ({ projectId, scheduleId, contractId }) =>
    apiAxios.post(`${scheduleUrl(projectId, scheduleId)}/link_contract`, { contract_id: contractId })
);

// Admin correction: move a plan (even released / linked) to the right buildings.
// The API may shrink or remove other empty plans, so the caller reloads the list.
export const reassignPaymentScheduleBuildings = thunk(
  "PaymentSchedule/reassignPaymentScheduleBuildings", 'Failed to move the payment plan',
  ({ projectId, scheduleId, projIds }) =>
    apiAxios.post(`${scheduleUrl(projectId, scheduleId)}/reassign_buildings`, { proj_ids: projIds })
);

export const createPaymentPlan = thunk(
  "PaymentSchedule/createPaymentPlan", 'Failed to create payment plan',
  ({ projectId, scheduleId, data }) => apiAxios.post(planUrl(projectId, scheduleId), { payment_plan: data })
);

export const updatePaymentPlan = thunk(
  "PaymentSchedule/updatePaymentPlan", 'Failed to update payment plan',
  ({ projectId, scheduleId, planId, data }) => apiAxios.put(planUrl(projectId, scheduleId, planId), { payment_plan: data })
);

export const deletePaymentPlan = thunk(
  "PaymentSchedule/deletePaymentPlan", 'Failed to delete payment plan',
  ({ projectId, scheduleId, planId }) => apiAxios.delete(planUrl(projectId, scheduleId, planId))
);

// Default To/Cc recipients + summary for the payment-plan email compose drawer.
export const getPaymentEmailDefaults = thunk(
  "PaymentSchedule/getPaymentEmailDefaults", 'Failed to load email defaults',
  ({ projectId, scheduleId }) => apiAxios.get(`${scheduleUrl(projectId, scheduleId)}/email_defaults`)
);

// Release: email the schedule (multipart: from/to[]/cc[]/subject/content/documents[]).
export const sendPaymentPlanEmail = thunk(
  "PaymentSchedule/sendPaymentPlanEmail", 'Failed to send payment plan email',
  ({ projectId, scheduleId, formData }) => apiAxiosUpload.post(`${scheduleUrl(projectId, scheduleId)}/send_email`, formData)
);

export const getBankGuaranteeEmailDefaults = thunk(
  "PaymentSchedule/getBankGuaranteeEmailDefaults", 'Failed to load email defaults',
  ({ projectId, scheduleId }) => apiAxios.get(`${guaranteeUrl(projectId, scheduleId)}/email_defaults`)
);

export const sendBankGuaranteeEmail = thunk(
  "PaymentSchedule/sendBankGuaranteeEmail", 'Failed to send bank guarantee',
  ({ projectId, scheduleId, formData }) => apiAxiosUpload.post(`${guaranteeUrl(projectId, scheduleId)}/send_email`, formData)
);

export const uploadBankGuaranteeDocuments = thunk(
  "PaymentSchedule/uploadBankGuaranteeDocuments", 'Failed to upload documents',
  ({ projectId, scheduleId, files }) => {
    const form = new FormData();
    files.forEach(file => form.append('documents[]', file));
    return apiAxiosUpload.post(`${guaranteeUrl(projectId, scheduleId)}/upload_document`, form);
  }
);

export const removeBankGuaranteeDocument = thunk(
  "PaymentSchedule/removeBankGuaranteeDocument", 'Failed to remove document',
  ({ projectId, scheduleId, documentId }) =>
    apiAxios.post(`${guaranteeUrl(projectId, scheduleId)}/remove_document`, { document_id: documentId })
);

const findSchedule = (state, id) => state.data.find(row => row.id === id);

// Whole-schedule responses ({ data: { attributes } }) replace / add the row.
const upsertSchedule = (state, action) => {
  const row = action.payload?.data?.attributes;
  if (!row) return;
  const index = state.data.findIndex(item => item.id === row.id);
  if (index === -1) state.data.push(row);
  else state.data[index] = row;
  if (action.payload.message) toast.success(action.payload.message);
};

const nextPlans = (plans, type, data) => {
  if (type === 'delete') return plans.filter(plan => plan.id !== data?.id);
  if (type === 'create') return [...plans, data];
  return plans.map(plan => (plan.id === data?.id ? data : plan));
};

const applyPlan = (type) => (state, action) => {
  const { payment_schedule_id, data, message } = action.payload || {};
  const schedule = findSchedule(state, payment_schedule_id);
  if (schedule) schedule.payment_plans = nextPlans(schedule.payment_plans || [], type, data);
  if (message) toast.success(message);
};

// Mọi endpoint BG/LC trả về nguyên trạng thái mới của khối, nên một reducer là đủ.
const applyBankGuarantee = (state, action) => {
  const { payment_schedule_id, message, ...guarantee } = action.payload || {};
  const schedule = findSchedule(state, payment_schedule_id);
  if (schedule) {
    schedule.bank_guarantee = {
      released:    guarantee.released,
      released_at: guarantee.released_at,
      released_by: guarantee.released_by,
      subject:     guarantee.subject,
      documents:   guarantee.documents || [],
      email_logs:  guarantee.email_logs || [],
      permissions: guarantee.permissions || {},
    };
  }
  if (message) toast.success(message);
};

const paymentScheduleSlice = createSlice({
  name: "crm_project_payment_schedule",
  initialState: {
    data: [],
    loading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentSchedules.pending, (state) => { state.loading = true; })
      .addCase(getPaymentSchedules.fulfilled, (state, action) => {
        state.loading = false;
        state.data = (action.payload || []).map(item => item.attributes);
      })
      .addCase(getPaymentSchedules.rejected, (state) => { state.loading = false; })
      .addCase(createPaymentSchedule.fulfilled, upsertSchedule)
      .addCase(updatePaymentSchedule.fulfilled, upsertSchedule)
      .addCase(linkPaymentScheduleContract.fulfilled, upsertSchedule)
      .addCase(reassignPaymentScheduleBuildings.fulfilled, upsertSchedule)
      .addCase(deletePaymentSchedule.fulfilled, (state, action) => {
        state.data = state.data.filter(row => row.id !== action.payload?.payment_schedule_id);
        if (action.payload?.message) toast.success(action.payload.message);
      })
      .addCase(createPaymentPlan.fulfilled, applyPlan('create'))
      .addCase(updatePaymentPlan.fulfilled, applyPlan('update'))
      .addCase(deletePaymentPlan.fulfilled, applyPlan('delete'))
      .addCase(getBankGuaranteeEmailDefaults.rejected, () => {})
      .addCase(sendBankGuaranteeEmail.fulfilled, applyBankGuarantee)
      .addCase(uploadBankGuaranteeDocuments.fulfilled, applyBankGuarantee)
      .addCase(removeBankGuaranteeDocument.fulfilled, applyBankGuarantee)
      .addCase(sendPaymentPlanEmail.fulfilled, (state, action) => {
        const { payment_schedule_id, data, milestone_release_info, message } = action.payload || {};
        const schedule = findSchedule(state, payment_schedule_id);
        if (schedule && data) {
          schedule.payment_plan_email_logs = [...(schedule.payment_plan_email_logs || []), data];
          if (milestone_release_info) schedule.milestone_release_info = milestone_release_info;
        }
        toast.success(message || 'Payment plan schedule was emailed successfully.');
      });

    // Add standard rejected action matcher using shared helper
    addRejectedMatcher(builder, "PaymentSchedule");
  }
});

export default paymentScheduleSlice.reducer;
